/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 16/03/22
 */

import { LoadingSpinner } from '@/components';
import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import FocusHeader from '@/components/FocusHeader/FocusHeader';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import usePageTitle from '@/utils/hooks/usePageTitle';
import { Box, Flex, Tab, TabList, TabPanel, TabPanels, Tabs, VStack } from '@chakra-ui/react';
import React, { useState } from 'react';
import { SubmittedWorkplans } from '@/components/pages/supervisor/SubmittedWorkplans';
import SupervisorUserSupervision from '@/components/pages/supervisor/SupervisorUserSupervision';
import { useQuery } from '@/utils/hooks/useQuery';
import { useSearchParams } from 'react-router-dom';

const PageTabs = [
    {
        key: 'submitted',
        name: 'Submitted Workplans',
        Content: SubmittedWorkplans,
        show: true,
    },
    {
        key: 'supervision',
        name: 'All Workplans',
        Content: SupervisorUserSupervision,
        show: true,
    },
];

const Supervisor = () => {
    usePageTitle('Supervisor');

    const { tab } = useQuery();
    const [, setSearch] = useSearchParams();
    const { currentAssignment, user, loading, error } = useAssignment();

    const [currentTab, setCurrentTab] = useState<number>(
        Math.max(
            PageTabs.findIndex((t) => t.key === tab),
            0,
        ),
    );

    const changeTab = (index: number) => {
        setCurrentTab(index);
        setSearch({ tab: PageTabs[index].key });
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error || !currentAssignment) {
        return <SimpleAlert variant="subtle">Could not load User Permissions</SimpleAlert>;
    }

    if (!user?.isSupervisor) {
        return (
            <Flex justifyContent="center" mt={6}>
                <SimpleAlert variant="top-accent" title="Not Authorised">
                    Not a supervisor.
                </SimpleAlert>
            </Flex>
        );
    }

    return (
        <Box maxH={'auto'} w="full" p={6}>
            <Box bg="offWhite" rounded="none" w="full">
                <VStack align="flex-start" spacing={6} w="full">
                    <FocusHeader size={'md'} textColor={'blue.500'} pt={6} px={6}>
                        Supervisor
                    </FocusHeader>
                    <Tabs variant={'enclosed'} isLazy index={currentTab} onChange={changeTab} w="full">
                        <TabList px={6} gridGap={6}>
                            {PageTabs.map((tab, i) => (
                                <Tab
                                    key={i}
                                    tabIndex={i}
                                    border={'1px'}
                                    borderColor={'gray.200'}
                                    fontWeight={i === currentTab ? 'semibold' : 'normal'}
                                >
                                    {tab.name}
                                </Tab>
                            ))}
                        </TabList>
                        <TabPanels>
                            {PageTabs.map((tab, i) => (
                                <TabPanel key={i} borderColor={'gray.100'} p={0}>
                                    <tab.Content />
                                </TabPanel>
                            ))}
                        </TabPanels>
                    </Tabs>
                </VStack>
            </Box>
        </Box>
    );
};

export default Supervisor;
